// AICA Theme Toggle Component
// Quick theme switching
// Bloomberg Terminal style: Efficient, accessible

import { Moon, Sun, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTheme, type Theme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

/**
 * Theme Toggle Button
 * Shows current theme and allows switching
 *
 * @example
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle() {
  const { t } = useTranslation(['settings', 'dashboard']);
  const { theme, setTheme, resolvedTheme } = useTheme();

  const themeOptions: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
    { value: 'light', label: t('settings:lightMode'), icon: Sun },
    { value: 'dark', label: t('settings:darkMode'), icon: Moon },
    { value: 'system', label: t('dashboard:ownerPanel.subTabs.system'), icon: Monitor },
  ];

  const currentIcon = theme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;
  const CurrentIcon = currentIcon;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-md"
          aria-label={t('dashboard:appSidebar.toggleTheme')}
        >
          <CurrentIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-2" align="end">
        <div className="space-y-1">
          {themeOptions.map((option) => {
            const Icon = option.icon;
            const isActive = theme === option.value;

            return (
              <button
                key={option.value}
                onClick={() => setTheme(option.value)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-button px-3 py-2 text-sm transition-all duration-150',
                  isActive
                    ? 'bg-primary text-bg font-medium'
                    : 'hover:bg-bg-elevated text-fg'
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-border">
          <p className="text-xs text-fg-muted px-3 py-1">{resolvedTheme}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Simple Theme Toggle (Icon only, no dropdown)
 * Cycles through light -> dark -> system
 */
export function SimpleThemeToggle() {
  const { t } = useTranslation('dashboard');
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => {
    const themeOrder: Theme[] = ['light', 'dark', 'system'];
    const currentIndex = themeOrder.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    const nextTheme = themeOrder[nextIndex];
    if (nextTheme) {
      setTheme(nextTheme);
    }
  };

  const Icon = theme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;

  return (
    <Button
      variant="ghost"
      size="icon-md"
      onClick={cycleTheme}
      aria-label={t('appSidebar.toggleTheme')}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}
